import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Switch } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { makeStyles } from '@material-ui/core/styles';
import { ACQUISITION_CARD_WIDTH, STEPPER_MAX_WIDTH } from '../../../theme';
import { Divider, Grid, Paper, Typography } from '@material-ui/core';
import SatelliteChooser from '../../components/acquisition/SatelliteChooser';
import AOIChooser from '../../components/csqueeze/AOIChooser';
import LandCover from '../../components/csqueeze/LandCover';
import WorkflowStepper from '../../components/steps/WorkflowStepper';
import StepSummary from '../../components/steps/StepSummary';
import Footer from '../../components/core/Footer';
import PeriodImageChooser from '../../components/acquisition/PeriodImageChooser';
import { Actions as Auth } from '../../../store/ducks/auth';
import { FIRST, useStepNavigations } from '../../../hooks/useStepNavigations';

/**
 * Module for the csqueeze process
 * @module Csqueeze
 */
/**
 * @constant MODULE_ACCENT
 * @description Csqueeze's identity color. Shared by the page header and the step indicator, so the
 * two read as one module instead of as two unrelated palettes.
 * @memberof module:Csqueeze
 */
const MODULE_ACCENT = '#687537';

const STEPS = [
  {
    label: 'forms.csqueeze.1.title',
    content: 'forms.csqueeze.1.description',
    component: (props) => <SatelliteChooser {...props} variant="modern" />,
  },
  {
    label: 'forms.csqueeze.2.title',
    content: 'forms.csqueeze.2.description',
    requires: 'satellite',
    component: AOIChooser,
  },
  {
    label: 'forms.csqueeze.3.title',
    content: 'forms.csqueeze.3.description',
    requires: 'geometry',
    props: { module: 'csqueeze', periodIndex: 0 },
    component: (props) => <PeriodImageChooser {...props} variant="modern" />,
  },
  {
    label: 'forms.csqueeze.4.title',
    content: 'forms.csqueeze.4.description',
    requires: 'geometry',
    props: { module: 'csqueeze', periodIndex: 1 },
    component: (props) => <PeriodImageChooser {...props} variant="modern" />,
  },
  {
    label: 'forms.csqueeze.5.title',
    content: 'forms.csqueeze.5.description',
    component: LandCover,
  },
];

/**
 * @function CoastalSqueezePage
 * @description Main component for the CSqueeze acquisition process.
 * The acquisition process is divided into multiple steps, each represented by a separate component.
 * @param {Object} props - Component properties passed from the router (match, location, history)
 * @see {@link SatelliteChooser} - Component for selecting satellite
 * @see {@link AOIChooser} - Component for defining area of interest (AOI)
 * @see {@link PeriodAndImageCChooser} - Component for refining image selection
 * @see {@link LandCover} - Component for land-cover classification
 * **Called by:
 * @see {MainPage} - via the `/csqueeze/:step?` Route (imported as CSqueeze).
 * @memberof module:Csqueeze
 * @returns {JSX.Element} Complete acquisition page JSX element
 */
const CoastalSqueezePage = (props) => {
  const dispatch = useDispatch();
  const [t] = useTranslation();
  const classes = useStyles();
  const { match } = props;
  const imagePeriods = useSelector((state) => state.csqueeze.imageList);
  const { acquisitionData, stepAttendsRequirements, navigate, navigateTo, canRevisitStep, makeRoutes } =
    useStepNavigations(match, STEPS, `/main/csqueeze/result`, 'csqueeze');

  useEffect(() => {
    dispatch(Auth.loadClientAuth());
  }, []);

  useEffect(() => {
    dispatch(Auth.begin());
    if (!stepAttendsRequirements()) navigate(FIRST);
  }, [dispatch]);

  const hasStep = match.params.step !== undefined;
  const step = hasStep ? match.params.step - 1 : 0;

  return (
    <div className={classes.wrapper}>
      <Grid container spacing={0} justifyContent="center">
        <Grid item xs={12} className={classes.header}>
          <div>
            <Typography className={classes.title} variant="h4" align="center">
              {t('forms.csqueeze.title')}
            </Typography>
          </div>
        </Grid>
        <Grid item className={classes.container}>
          <Paper className={classes.content} elevation={1}>
            <WorkflowStepper
              className={classes.stepper}
              accentColor={MODULE_ACCENT}
              steps={STEPS.map((el, i) => ({ id: String(i), label: t(el.label), disabled: !canRevisitStep(i) }))}
              currentStep={String(step)}
              onStepClick={(i) => canRevisitStep(i) && navigateTo(i)}
            />
            <StepSummary acquisition={acquisitionData} imagePeriods={imagePeriods} className={classes.summary} />
            {STEPS[step].content && (
              <Typography className={classes.instructions} align="center">
                {t(STEPS[step].content)}
              </Typography>
            )}
            <Divider />
            <Switch>{makeRoutes(hasStep, STEPS[step]?.props)}</Switch>
          </Paper>
        </Grid>
      </Grid>
      <Footer />
    </div>
  );
};

/**
 * @function useStyles
 * @description Hook for styling using Material-UI's makeStyles. Defines CSS styles for the
 * CSqueeze acquisition wizard.
 * @param {Object} theme - Material-UI theme containing color, spacing, and other configurations
 * @memberof module:Csqueeze
 * @returns {Object} Object containing compiled CSS classes for component usage
 */
const useStyles = makeStyles((theme) => ({
  wrapper: {
    display: 'flex',
    flexFlow: 'column',
    flexGrow: 1,
  },
  header: {
    backgroundColor: MODULE_ACCENT,
    padding: theme.spacing(4, 0, 12),
  },
  title: {
    color: 'white',
  },
  container: {
    display: 'flex',
    alignItems: 'center',
    flexFlow: 'column',
  },
  content: {
    width: ACQUISITION_CARD_WIDTH,
    marginTop: theme.spacing(-9),
  },
  summary: {
    marginBottom: theme.spacing(1),
  },
  instructions: {
    paddingTop: theme.spacing(1),
    marginBottom: theme.spacing(3),
    whiteSpace: 'pre-line',
  },
  stepper: {
    maxWidth: STEPPER_MAX_WIDTH,
    padding: theme.spacing(4, 3, 1.5),
  },
}));
export default CoastalSqueezePage;
